<script lang="ts">
  import { api } from '../../lib/api/client';
  import type { Insights, PlaceCategory } from '../../lib/api/types';
  import Bars from '../../lib/components/charts/Bars.svelte';
  import Chart from '../../lib/components/charts/Chart.svelte';
  import Readout from '../../lib/components/charts/Readout.svelte';
  import { measureAxis } from '../../lib/utils/insights';
  import { locale, month as monthOf, t } from '../../lib/i18n/index.ts';
  import { categoryIcon, categoryLabel, distance, modeColors, modeIcon, modeLabel, span, timelineBase } from './format';

  // Insights shows a month's or a year's travel per way, a bar per month of
  // the year, and time and places per category (005 FR-037, 005 FR-038). Picking a
  // category opens its places. The months' chart is in the shared chart
  // language (spec 010 FR-033): a distance scale, and hovering or
  // tapping a month reads each way's distance above it.
  let { groupId, userId, self, onCategory }: {
    groupId?: string;
    userId: string;
    self: boolean;
    onCategory: (c: PlaceCategory, period: string) => void;
  } = $props();

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date();
  let byYear = $state(false);
  let month = $state(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  let year = $state(String(now.getFullYear()));
  let data = $state<Insights | null>(null);
  let error = $state('');

  const period = $derived(byYear ? year : month);
  const colors = modeColors;

  $effect(() => {
    load(period);
  });

  async function load(p: string) {
    error = '';
    try {
      const got = await api.get<Insights>(`${timelineBase(groupId, userId, self)}/insights?period=${p}&tz=${encodeURIComponent(tz)}`);
      if (p === period) data = got;
    } catch (e: any) {
      error = e.message;
      data = null;
    }
  }

  const travelled = $derived(data?.modes.filter((m) => m.seconds > 0) ?? []);
  const axis = $derived(measureAxis('distance'));
  const monthTotal = (mo: Insights['months'][number]) => mo.modes.reduce((a, x) => a + x.distance_m, 0);
  const scale = $derived(axis.scale(Math.max(0, ...(data?.months.map(monthTotal) ?? [0])) * axis.factor));
  const positions = $derived(data ? data.months.map((_, i) => (i + 0.5) / data!.months.length) : []);
  const monthLabel = (m: string) => monthOf(new Date(`${m}-15T12:00:00`), true);
  const monthLong = (m: string) => new Date(`${m}-15T12:00:00`).toLocaleDateString(locale(), { month: 'long', year: 'numeric' });
  // modesText lists a month's ways of travel with their distances.
  const modesText = (mo: Insights['months'][number]) => {
    const items = mo.modes.filter((m) => m.distance_m > 0).map((m) => t('timelineModeAndDistance', { mode: modeLabel(m.mode), distance: distance(m.distance_m) }));
    return items.length ? new Intl.ListFormat(locale(), { type: 'unit', style: 'short' }).format(items) : t('timelineNoTravel');
  };
  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  const shown = $derived(hover ?? at);
  // stacks are each month's ways piled in order, each bar from the ways below it.
  const stacks = $derived(data ? data.months.flatMap((mo, i) => {
    let below = 0;
    const selected = byYear || mo.month === month;
    return mo.modes.filter((m) => m.distance_m > 0).map((m) => {
      const bar = { at: positions[i], base: below * axis.factor, v: (below + m.distance_m) * axis.factor, color: colors[m.mode], opacity: selected ? 1 : 0.45 };
      below += m.distance_m;
      return bar;
    });
  }) : []);
  const reading = $derived(data && shown != null ? data.months[shown] : null);
</script>

<div class="flex-1 overflow-y-auto p-6">
  <div class="max-w-3xl mx-auto space-y-6">
    <div class="flex items-center gap-3">
      <div class="flex gap-1" role="tablist" aria-label={t('timelinePeriod')}>
        <button role="tab" aria-selected={!byYear} class="btn btn-sm {byYear ? 'btn-secondary' : 'btn-primary'}" onclick={() => (byYear = false)}>{t('timelineMonth')}</button>
        <button role="tab" aria-selected={byYear} class="btn btn-sm {byYear ? 'btn-primary' : 'btn-secondary'}" onclick={() => (byYear = true)}>{t('timelineYear')}</button>
      </div>
      {#if byYear}
        <input type="number" class="input w-28" min="2000" max={now.getFullYear()} bind:value={year} aria-label={t('timelineYear')} />
      {:else}
        <input type="month" class="input w-48" bind:value={month} aria-label={t('timelineMonth')} />
      {/if}
    </div>

    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {:else if data}
      <section class="card p-5">
        <h2 class="font-semibold mb-3">{t('timelineTravel')}</h2>
        {#if travelled.length === 0}
          <p class="text-sm text-text-muted">{t('timelineNoTravelIn', { period: byYear ? 'year' : 'month' })}</p>
        {:else}
          <ul class="grid grid-cols-2 gap-3">
            {#each travelled as m (m.mode)}
              <li class="flex items-center gap-3">
                <i aria-hidden="true" class="las {modeIcon(m.mode)} text-2xl" style="color: {colors[m.mode]};"></i>
                <span>
                  <span class="block font-medium">{modeLabel(m.mode)}</span>
                  <span class="block text-sm text-text-muted">{t('timelineDistanceTime', { distance: distance(m.distance_m), time: span(m.seconds) })}</span>
                </span>
              </li>
            {/each}
          </ul>
        {/if}
        <div class="mt-5 space-y-2">
          <Readout title={reading ? t('timelineReading', { label: monthLong(reading.month), value: distance(monthTotal(reading)) }) : t('timelineHoverMonth')} idle={!reading}
            items={(reading?.modes ?? data.modes).filter((m) => m.distance_m > 0).map((m) => ({ label: modeLabel(m.mode), value: reading ? distance(m.distance_m) : undefined, color: colors[m.mode], mark: 'bar' as const }))} />
          <Chart lo={scale.lo} hi={scale.hi} unit={axis.unit} yTicks={scale.ticks.map((t) => ({ v: t, text: axis.tick(t, scale.step) }))}
            xTicks={data.months.map((mo, i) => ({ at: positions[i], text: monthLabel(mo.month) }))} xGrid={false} height="h-36" {positions} bind:at bind:hover
            label={t('timelineMonthDistance', { year: data.months[0]?.month.slice(0, 4) })}
            valueText={reading ? t('timelineReading', { label: monthLong(reading.month), value: modesText(reading) }) : t('timelineNoMonthPicked')}>
            {#snippet overlay()}
              <Bars bars={stacks} width={0.6 / Math.max(1, data!.months.length)} lo={scale.lo} hi={scale.hi} />
            {/snippet}
          </Chart>
        </div>
      </section>

      <section class="card p-5">
        <h2 class="font-semibold mb-3">{t('timelineVisits')}</h2>
        {#if data.categories.length === 0}
          <p class="text-sm text-text-muted">{t('timelineNoStaysIn', { period: byYear ? 'year' : 'month' })}</p>
        {:else}
          <ul>
            {#each data.categories as c (c.category)}
              <li>
                <button class="w-full text-left py-2 flex items-center gap-3 hover:opacity-80" onclick={() => onCategory(c.category, period)}>
                  <i aria-hidden="true" class="las {categoryIcon(c.category)} text-xl"></i>
                  <span class="flex-1">{categoryLabel(c.category)}</span>
                  <span class="text-sm text-text-muted">{t('timelinePlacesTime', { places: c.places, time: span(c.seconds) })}</span>
                  <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </section>
    {/if}
  </div>
</div>
